import type { LinkedModuleInfo } from '@hibro/platform-renderer';
import type {
  BundleTreeFile,
  ModuleManifest,
  ModuleRuntime,
} from '@hibro/shared';
import {
  MODULE_STAGED_SUBDIRS,
  resolveModuleRuntimes,
  splitSafeAssetPath,
  stagedPrebuiltServicePaths,
} from '@hibro/shared';

import { OpfsLinkedStore } from '../ext-host-web/linked-store-opfs';

/**
 * Load-unpacked dev modules for the browser runtime (见 11 调试 / 18) — the page
 * half, which is where this whole flow has to live.
 *
 * **Why the page and not the host.** A local directory is only reachable through
 * a `FileSystemDirectoryHandle`, and a handle restored from IndexedDB usually
 * reports `prompt` until `requestPermission()` is called — which throws outside
 * transient user activation, and a worker can never have any (measured, and the
 * spec says so). The Link and Reload buttons *are* that activation, so the page
 * owns the handle, re-grants when needed, and copies the tree into OPFS; the
 * host and the Service Worker then read OPFS exactly as they do for a delivered
 * module, with no permission concept anywhere downstream.
 *
 * **Why a copy rather than a live mount.** Desktop mounts the working directory
 * by reference, so a rebuilt `dist/` is visible on the next refresh. Serving
 * `/_mod/<id>/*` off a handle would need the Service Worker to hold a granted
 * permission it cannot request, so the copy is not a shortcut but the only
 * shape that does not fail as "missing files" whenever a grant lapses. The cost
 * is one extra click after a rebuild; front-end iteration is unaffected, since
 * a linked module's `views[].url` can point at the author's own dev server.
 */

/** IndexedDB database holding picked directory handles, keyed by module id. */
const HANDLE_DB = 'hibro-linked-handles';
const HANDLE_STORE = 'handles';

/**
 * The slice of `FileSystemDirectoryHandle` used to read a picked working
 * directory. Narrowed like every other handle type in this codebase so the
 * traversal can be unit-tested against a small in-memory fake.
 *
 * The two permission methods are optional because they are the part with the
 * least settled support; absent means "assume reachable and let the read fail",
 * which is the honest degradation.
 */
export interface PickedDirectoryHandle {
  readonly kind: 'directory';
  readonly name: string;
  values(): AsyncIterable<PickedHandle>;
  queryPermission?(descriptor: {
    mode: 'read';
  }): Promise<PermissionState | undefined>;
  requestPermission?(descriptor: {
    mode: 'read';
  }): Promise<PermissionState | undefined>;
}

export interface PickedFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<{ arrayBuffer(): Promise<ArrayBuffer> }>;
}

export type PickedHandle = PickedDirectoryHandle | PickedFileHandle;

/** Conventional manifest filename at a module root (架构方案 §5.3). */
const MODULE_MANIFEST_FILENAME = 'module.manifest.json';

/**
 * Why a copied module will not register on this host (见 12 / 18). The page
 * can copy any delivered tree; `ModuleManager.runtimeProblems` is what
 * actually refuses it. Surfacing the same predicate here is what stops
 * "Link succeeded, rail never appeared" from looking like a copy bug.
 */
export function linkedRuntimeNote(
  manifest: Pick<ModuleManifest, 'runtimes'>,
  runtime: ModuleRuntime = 'web',
): string | undefined {
  const runtimes = resolveModuleRuntimes(manifest);
  if (runtimes.includes(runtime)) {
    return undefined;
  }
  return `declares [${runtimes.join(', ')}] — skipped on this ${runtime} host`;
}

declare global {
  interface Window {
    /**
     * File System Access directory picker. Declared here because the TS DOM lib
     * this repo builds against does not carry it yet, and narrowed to the
     * handle slice above rather than the full DOM type — the call is guarded by
     * an `in window` check at the only call site.
     */
    showDirectoryPicker?(options?: {
      mode?: 'read' | 'readwrite';
    }): Promise<PickedDirectoryHandle>;
  }
}

/**
 * Make sure the handle can be read, prompting only when `interactive`.
 *
 * `requestPermission` **must** be reached synchronously enough to still be
 * inside the click that started this, which is why every caller of the
 * interactive form is a button handler and none of them awaits anything first.
 */
export async function ensureReadAccess(
  handle: PickedDirectoryHandle,
  interactive: boolean,
): Promise<boolean> {
  const query = handle.queryPermission?.bind(handle);
  if (!query) {
    // No permission API to consult: attempt the read and let it fail honestly.
    return true;
  }
  if ((await query({ mode: 'read' })) === 'granted') {
    return true;
  }
  if (!interactive) {
    return false;
  }
  const request = handle.requestPermission?.bind(handle);
  return request ? (await request({ mode: 'read' })) === 'granted' : false;
}

/**
 * Budget for one linked copy.
 *
 * Its own constant rather than the delivered-bundle cap: this walk runs **in the
 * page** and materializes every file it reads, so it is bounded by a tab's heap
 * rather than by what a deployment is willing to install (见 12 本地资源侧载).
 * A working directory's delivered shape is a dev-loop artifact; if one is
 * genuinely this large, the author wants a `.tgz` install, not Link.
 */
export const MAX_LINKED_COPY_BYTES = 64 * 1024 * 1024;

/** A module's delivered shape, read out of a picked working directory. */
export interface PickedModule {
  moduleId: string;
  entries: BundleTreeFile[];
}

async function readEntry(
  handle: PickedFileHandle,
  segments: string[],
): Promise<BundleTreeFile> {
  const file = await handle.getFile();
  return { segments, data: new Uint8Array(await file.arrayBuffer()) };
}

async function listChildren(
  dir: PickedDirectoryHandle,
): Promise<Map<string, PickedHandle>> {
  const children = new Map<string, PickedHandle>();
  for await (const child of dir.values()) {
    children.set(child.name, child);
  }
  return children;
}

/** Count a file against the copy budget. The same ceiling the directory walk uses. */
function take(entry: BundleTreeFile, budget: { remaining: number }): void {
  budget.remaining -= entry.data.byteLength;
  if (budget.remaining < 0) {
    throw new Error(
      `linked module is larger than ${Math.floor(MAX_LINKED_COPY_BYTES / (1024 * 1024))} MiB; only a module's delivered shape is copied, so this usually means a build artifact landed in ${MODULE_STAGED_SUBDIRS.join('/')}`,
    );
  }
}

/** Recursively collect every file under `dir`, prefixed with `base`. */
async function collectDir(
  dir: PickedDirectoryHandle,
  base: string[],
  into: BundleTreeFile[],
  budget: { remaining: number },
): Promise<void> {
  for await (const child of dir.values()) {
    const segments = [...base, child.name];
    // The same segment policy the asset route and the tar unpacker apply, so a
    // file that cannot be addressed as `/_mod/<id>/<path>` is never written.
    if (!splitSafeAssetPath(segments.join('/'))) {
      continue;
    }
    if (child.kind === 'directory') {
      await collectDir(child, segments, into, budget);
      continue;
    }
    const entry = await readEntry(child, segments);
    take(entry, budget);
    into.push(entry);
  }
}

/**
 * Read one declared service file out of the picked root. A directory, or a
 * path that is not there, fails the link: the sandbox reads this exact file
 * after the copy, and an empty activation would look like a successful link.
 */
async function readDeclaredService(
  moduleId: string,
  rootChildren: Map<string, PickedHandle>,
  relativePath: string,
  budget: { remaining: number },
): Promise<BundleTreeFile> {
  const segments = relativePath.split('/');
  let handle: PickedHandle | undefined;
  let listing = rootChildren;
  for (const [i, name] of segments.entries()) {
    handle = listing.get(name);
    const last = i === segments.length - 1;
    if (!handle || (last && handle.kind !== 'file')) {
      handle = undefined;
      break;
    }
    if (!last) {
      if (handle.kind !== 'directory') {
        handle = undefined;
        break;
      }
      listing = await listChildren(handle);
    }
  }
  if (!handle || handle.kind !== 'file') {
    throw new Error(
      `module "${moduleId}" declares entry.service "${relativePath}" but that file is not in the module root`,
    );
  }
  const entry = await readEntry(handle, segments);
  take(entry, budget);
  return entry;
}

/**
 * Read a picked directory's **delivered shape** — the manifest plus
 * {@link MODULE_STAGED_SUBDIRS}, plus a prebuilt `entry.service` that lives
 * outside those directories — and nothing else.
 *
 * That restriction is the whole reason this is not a plain recursive copy: a
 * developer's working directory is where `node_modules/` lives, and walking it
 * into OPFS would copy tens of thousands of files that no runtime would ever
 * read. It is the same rule forge stages with and the web build copies with.
 */
export async function readPickedModule(
  dir: PickedDirectoryHandle,
): Promise<PickedModule> {
  const entries: BundleTreeFile[] = [];
  const budget = { remaining: MAX_LINKED_COPY_BYTES };
  const children = await listChildren(dir);

  const manifestHandle = children.get(MODULE_MANIFEST_FILENAME);
  if (!manifestHandle || manifestHandle.kind !== 'file') {
    throw new Error(
      `"${dir.name}" has no ${MODULE_MANIFEST_FILENAME}; pick the module's root directory`,
    );
  }
  const manifestEntry = await readEntry(manifestHandle, [
    MODULE_MANIFEST_FILENAME,
  ]);
  entries.push(manifestEntry);

  const parsed: unknown = JSON.parse(
    new TextDecoder().decode(manifestEntry.data),
  );
  const body = parsed as {
    id?: unknown;
    entry?: ModuleManifest['entry'];
  } | null;
  const moduleId = body?.id;
  if (typeof moduleId !== 'string' || moduleId === '') {
    throw new Error(
      `${MODULE_MANIFEST_FILENAME} in "${dir.name}" declares no id`,
    );
  }
  // The id becomes an OPFS directory name and a `/_mod/<id>/…` authority, so it
  // goes through the same guard every other untrusted segment does.
  if (!splitSafeAssetPath(moduleId)?.length) {
    throw new Error(`module id "${moduleId}" is not a usable directory name`);
  }

  for (const subdir of MODULE_STAGED_SUBDIRS) {
    const child = children.get(subdir);
    if (child?.kind === 'directory') {
      await collectDir(child, [subdir], entries, budget);
    }
  }
  let servicePaths: string[];
  try {
    servicePaths = stagedPrebuiltServicePaths(body?.entry, 'web');
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    throw new Error(`module "${moduleId}": ${reason}`, { cause: err });
  }
  for (const rel of servicePaths) {
    entries.push(await readDeclaredService(moduleId, children, rel, budget));
  }
  return { moduleId, entries };
}

/** IndexedDB reports failure as a nullable `DOMException`; callers want an Error. */
function idbError(what: string, error: DOMException | null): Error {
  return new Error(`${what}: ${error?.message ?? 'IndexedDB request failed'}`);
}

/** Persisted picked directories, so Reload does not re-open the picker. */
class HandleStore {
  private db?: Promise<IDBDatabase>;

  private open(): Promise<IDBDatabase> {
    this.db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(HANDLE_DB, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore(HANDLE_STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(idbError('opening the linked-handle store', request.error));
    });
    return this.db;
  }

  private async run<T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const request = work(
        db.transaction(HANDLE_STORE, mode).objectStore(HANDLE_STORE),
      );
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(idbError('reading a linked-module handle', request.error));
    });
  }

  get(moduleId: string): Promise<PickedDirectoryHandle | undefined> {
    return this.run('readonly', (store) => store.get(moduleId)) as Promise<
      PickedDirectoryHandle | undefined
    >;
  }

  async put(moduleId: string, handle: PickedDirectoryHandle): Promise<void> {
    await this.run('readwrite', (store) => store.put(handle, moduleId));
  }

  async delete(moduleId: string): Promise<void> {
    await this.run('readwrite', (store) => store.delete(moduleId));
  }
}

export interface LinkedModulesOptions {
  /**
   * Tell the host the linked set changed. It re-reads OPFS rather than trusting
   * a list from here — every tab shares one origin-private tree, so the tree is
   * the single source of truth and a page announcing its own view could only
   * ever be a stale copy of it.
   */
  onChanged: () => void;
  /** Diagnostics sink (见 15). */
  log: (message: string, error?: unknown) => void;
  /** Injected for tests; defaults to the real picker. */
  pickDirectory?: () => Promise<PickedDirectoryHandle | undefined>;
  store?: OpfsLinkedStore;
}

/**
 * The renderer-facing bridge. Mirrors the desktop Developer section's
 * link/unlink/reload verbs (见 11 调试) without borrowing its bridge: that one
 * also carries `setDebug` / `openDevtools` / `setDiagnostics`, all of which are
 * Electron-only, so the two runtimes inject different, smaller seams instead.
 */
export interface LinkedModulesApi {
  list(): Promise<LinkedModuleInfo[]>;
  link(): Promise<LinkedModuleInfo | undefined>;
  reload(moduleId: string): Promise<void>;
  unlink(moduleId: string): Promise<void>;
}

export function createLinkedModules(
  options: LinkedModulesOptions,
): LinkedModulesApi {
  const store = options.store ?? new OpfsLinkedStore();
  const handles = new HandleStore();
  const pickDirectory =
    options.pickDirectory ??
    (async () => {
      try {
        return await window.showDirectoryPicker?.({ mode: 'read' });
      } catch (err) {
        // The picker rejects with AbortError when dismissed, which is an
        // ordinary outcome and must not surface as a failure.
        if (err instanceof Error && err.name === 'AbortError') {
          return undefined;
        }
        throw err;
      }
    });

  /** Copy a picked directory in, replacing whatever was there. */
  const copyIn = async (
    dir: PickedDirectoryHandle,
  ): Promise<LinkedModuleInfo> => {
    const { moduleId, entries } = await readPickedModule(dir);
    const manifest = await store.write(moduleId, entries);
    await handles.put(moduleId, dir);
    options.onChanged();
    return {
      id: moduleId,
      dir: dir.name,
      ...(manifest ? { warning: linkedRuntimeNote(manifest) } : {}),
    };
  };

  return {
    async list() {
      const ids = await store.list();
      const manifests = new Map(
        (await store.read(ids)).map((mod) => [mod.moduleId, mod.manifest]),
      );
      const infos: LinkedModuleInfo[] = [];
      for (const id of ids) {
        const handle = await handles.get(id).catch(() => undefined);
        const manifest = manifests.get(id);
        const warning = manifest ? linkedRuntimeNote(manifest) : undefined;
        infos.push({
          id,
          dir: handle?.name ?? id,
          // Surfaced so the row can say *why* Reload is needed, rather than
          // letting the click look like it did nothing.
          ...(handle && !(await ensureReadAccess(handle, false))
            ? { needsPermission: true }
            : {}),
          ...(warning ? { warning } : {}),
        });
      }
      return infos;
    },

    async link() {
      const dir = await pickDirectory();
      if (!dir) {
        return undefined;
      }
      // Still inside the click that opened the picker, so a prompt is allowed.
      if (!(await ensureReadAccess(dir, true))) {
        throw new Error('read access to the picked directory was refused');
      }
      return copyIn(dir);
    },

    async reload(moduleId) {
      const dir = await handles.get(moduleId);
      if (!dir) {
        throw new Error(
          `no picked directory is remembered for "${moduleId}"; unlink it and link it again`,
        );
      }
      if (!(await ensureReadAccess(dir, true))) {
        throw new Error(
          `read access to "${dir.name}" was refused; the browser forgets it after a while and only a click can restore it`,
        );
      }
      const { id: found } = await copyIn(dir);
      if (found !== moduleId) {
        // The manifest was edited to a different id: the old copy would linger
        // forever with nothing pointing at it.
        await store.remove(moduleId);
        await handles.delete(moduleId);
        options.log(
          `linked module "${moduleId}" now declares id "${found}"; the old copy was dropped`,
        );
        options.onChanged();
      }
    },

    async unlink(moduleId) {
      await store.remove(moduleId);
      await handles.delete(moduleId);
      options.onChanged();
    },
  };
}
